import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { CMultiSelect, CSpinner } from '@coreui/react-pro'
import { updateTenderChapterSupplierContacts } from 'src/services/requests/tenders/tenders'
import { GlobalVariablesContext } from 'src/contexts/globalVariableContext'

const MultiSelector = ({ t, id, tenderStatus, suppliers }) => {
  const { setError, suppliersLoaded } = useContext(GlobalVariablesContext)
  const [isLoading, setIsLoading] = useState(false)
  const [inboundSuppliers, setInboundSuppliers] = useState([])
  const [alreadySelectedContacts, setAlreadySelectedContacts] = useState(null)

  const updateChapterSupplierContacts = async (id, data) => {
    return new Promise((resolve) => {
      setIsLoading(true)
      updateTenderChapterSupplierContacts(id, data)
        .then((response) => {
          resolve(response)
        })
        .catch((exception) => {
          setError(exception?.response?.data?.error)
        })
        .finally(() => {
          setIsLoading(false)
        })
    })
  }

  const isDisabled = useMemo(() => (
    tenderStatus === 'emails_sent' ||
    tenderStatus === 'in_progress' ||
    !inboundSuppliers.length ||
    !suppliersLoaded
  ), [tenderStatus, inboundSuppliers.length, suppliersLoaded]);

  const filterSelectedContacts = (initialSuppliers) => {
    const initialSelectedContacts = [];

    for (const supplier of initialSuppliers) {
      if (supplier.options) {
        for (const supplierOption of supplier.options) {
          if (supplierOption.selected) {
            initialSelectedContacts.push(supplierOption.value);
          }
        }
      }
    }

    setInboundSuppliers(initialSuppliers);
    setAlreadySelectedContacts(initialSelectedContacts);
  };


  const hasSelectionChanges = (currentSelection) => {

    if (alreadySelectedContacts === null && !currentSelection.length) {
      setAlreadySelectedContacts(currentSelection)
      return false;
    }

    const prevSet = new Set(alreadySelectedContacts);
    const newSet = new Set(currentSelection);

    if (prevSet.size !== newSet.size) {
      setAlreadySelectedContacts(currentSelection);
      return true;
    }

    for (const id of prevSet) {
      if (!newSet.has(id)) {
        setAlreadySelectedContacts(currentSelection);
        return true;
      }
    }

    for (const id of newSet) {
      if (!prevSet.has(id)) {
        setAlreadySelectedContacts(currentSelection);
        return true;
      }
    }

    return false;
  };


  const renderGroupTemplate = useCallback((option) => (
    <div className={`group-wrapper group-wrapper-${option.code}`} key={option.label}>
      {option.label}
    </div>
  ), []);

  const renderOptionTemplate = useCallback((option) => (
    <div className={`option ${option.label ? `option-${option.label}` : ''}`} key={option.label}>
      {option.label.split('(')[0]}
    </div>
  ), []);

  const sanitizeSuppliers = (suppliers) => {
    return suppliers.map(group => ({
      ...group,
      options: (group.options || []).filter(opt => opt.value && opt.label)
    }));
  };

  useEffect(() => {
    if (suppliers.length) {
      const sanitized = sanitizeSuppliers(suppliers);
      filterSelectedContacts(sanitized);
    }
  }, [suppliers]);

  return (
    inboundSuppliers.length ?
    <CMultiSelect
      id={id}
      className='w-100 supplier-multiselector'
      loading={isLoading}
      disabled={isDisabled}
      clearSearchOnSelect
      placeholder={t('SupplierContacts')}
      searchNoResultsLabel={t('NoResultsFound')}
      selectAll={false}
      options={inboundSuppliers}
      optionsStyle='text'
      optionsGroupsTemplate={renderGroupTemplate}
      optionsTemplate={renderOptionTemplate}
      onChange={(option) => {
        const suppContacts = option.map((value) => value.value);
        if (hasSelectionChanges(suppContacts)) updateChapterSupplierContacts(id, suppContacts);
      }}
    /> : <div className='w-100'><CSpinner color='primary' variant='grow' id={`${id}_inner_loader`} /></div>
  )
}

export default MultiSelector
